import React from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import Overlay from "../Overlay";
import { sweetalert } from "../../App";
import AdminAPI from "../../network/AdminAPI";
import { DashboardHost } from "../../dashboards/DashboardHost";
import { AudienceBadge } from "../../dashboards/AudienceBadge";
// The filter row is styled by the same stylesheet as the tiles, but it
// renders before any dashboard does — and viz.css otherwise arrives only
// with the lazy tile kit, so the row would flash unstyled (or stay that
// way for a dashboard that imports no tiles). Imported here, not in
// DashboardHost, because this is the component that owns the row.
import "../../dashboards/tiles/viz.css";
import {
    DashboardMount,
    DashboardParam,
    DashboardSummary,
    DashboardTileData,
} from "../../dashboards/types";

// =====================================================================
// Dashboards — the internal dashboard viewer (the Power BI replacement).
// ---------------------------------------------------------------------
// Owns everything a dashboard component must NOT own: the catalog, the
// filter row, fetching, and loading state. It hands a component nothing
// but rows.
//
// Backend: /api/admin/dashboards/list and /data. Audience filtering and
// facility scoping are enforced server-side; this page never sees SQL and
// never decides who may see what.
//
// Param inputs are modelled on the deleted Reports.tsx so the page still feels
// like the rest of the portal. That page and the business-report PDF path it
// served are removed — dashboards replace them. (Lab RESULT PDFs are a separate
// system and untouched.)
//
// ONE page, two mounts. `mount` selects which API surface to call:
//   'internal' -> staff, /api/admin/dashboards        (AdminPages.Dashboards)
//   'external' -> providers, /api/admin/providerDashboards
//                                            (AdminPages.ProviderDashboards)
// Parameterized rather than copied so the two audiences cannot drift apart
// in behavior. Which dashboards each mount may see is the server's call.
// =====================================================================

interface DashboardsProps {
    mount?: DashboardMount;
}

interface DashboardsState {
    dashboards: DashboardSummary[];
    selected?: DashboardSummary;
    paramValues: { [key: string]: any };
    facilityNamesMapped: { label: string; value: number }[];
    data: DashboardTileData;
    tileErrors?: { [tileKey: string]: string };
    generatedAt?: string;
    // Distinguishes "page is booting" from "tiles are refetching" — the
    // latter holds the previous render instead of blanking it.
    showLoading: boolean;
    refetching: boolean;
    hasRun: boolean;
}

export class Dashboards extends React.Component<DashboardsProps, DashboardsState> {
    // Monotonic request token. Clearing state in onSelectDashboard covers the
    // CLICK but not the request already in flight: without this, dashboard A's
    // rows resolve after the switch and render under dashboard B's title. Tile
    // keys like `totals` are generic enough to overlap, so the result is wrong
    // numbers rather than an obviously broken page. Also stops
    // setState-after-unmount when someone navigates away mid-fetch.
    private requestId = 0;
    private mounted = true;

    private get mount(): DashboardMount {
        return this.props.mount || "internal";
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    // Ignore anything that is no longer the newest request, or that arrives
    // after unmount.
    private isCurrent(id: number): boolean {
        return this.mounted && id === this.requestId;
    }

    constructor(props) {
        super(props);
        this.state = {
            dashboards: [],
            selected: undefined,
            paramValues: {},
            facilityNamesMapped: [],
            data: {},
            tileErrors: undefined,
            generatedAt: undefined,
            showLoading: false,
            refetching: false,
            hasRun: false,
        };
        this.run = this.run.bind(this);
        this.clear = this.clear.bind(this);
    }

    componentDidMount() {
        this.setState({ showLoading: true }, async () => {
            try {
                // allSettled, not all: the facility list only populates
                // `type: 'facility'` param inputs, so losing it must not blank
                // the whole page for dashboards that have no such param.
                const [listResult, facResult] = await Promise.allSettled([
                    AdminAPI.getDashboardsList(this.mount),
                    AdminAPI.getFacilitiesForSubmissionForm(),
                ]);

                const list = listResult.status === "fulfilled" ? listResult.value : null;
                if (!list || !list.success) {
                    this.setState({ showLoading: false });
                    return sweetalert.fire({
                        icon: "error",
                        title: "",
                        text: (list && list.reason) || "Unable to load dashboards.",
                    });
                }

                if (facResult.status === "rejected") {
                    // Non-fatal: a facility picker will render empty and the
                    // user can still run everything else.
                    console.error("Unable to load the facility list", facResult.reason);
                }
                const facData = facResult.status === "fulfilled" ? facResult.value : null;

                // De-dupe by facility ID. `new Set(objects)` compares by
                // reference and every mapped object is fresh, so the Set this
                // replaced never removed anything (carried over from Reports.tsx).
                const byId = new Map<number, { label: string; value: number }>();
                for (const f of (facData && facData.authorizedFacilities) || []) {
                    if (f && f.FacilityName && !byId.has(f.ID)) {
                        byId.set(f.ID, { label: f.FacilityName, value: f.ID });
                    }
                }

                this.setState({
                    dashboards: list.dashboards || [],
                    facilityNamesMapped: Array.from(byId.values()),
                    showLoading: false,
                });
            } catch (e) {
                console.error(e);
                this.setState({ showLoading: false });
                sweetalert.fire({
                    icon: "error",
                    title: "",
                    text: "Unable to load dashboards.",
                });
            }
        });
    }

    onSelectDashboard(name: string) {
        const selected = this.state.dashboards.find((d) => d.name === name);
        // Switching dashboards resets params AND data. Bumping requestId is the
        // other half: it invalidates any response still in flight, so the
        // previous dashboard's rows can't land under this one's title.
        this.requestId++;
        this.setState({
            selected,
            paramValues: {},
            data: {},
            tileErrors: undefined,
            generatedAt: undefined,
            hasRun: false,
        });
    }

    setParam(key: string, value: any) {
        this.setState((prev) => ({
            paramValues: { ...prev.paramValues, [key]: value },
        }));
    }

    clear() {
        this.setState({
            selected: undefined,
            paramValues: {},
            data: {},
            tileErrors: undefined,
            generatedAt: undefined,
            hasRun: false,
        });
    }

    // Convert a param value into what the backend query binds.
    private formatValue(param: DashboardParam, value: any) {
        if (value === undefined || value === null || value === "") return null;
        if (param.type === "date" && value instanceof Date) {
            return moment(value).format("YYYY-MM-DD");
        }
        return value;
    }

    run() {
        const dashboard = this.state.selected;
        if (!dashboard) {
            return sweetalert.fire({
                icon: "info",
                title: "",
                text: "Please select a dashboard.",
            });
        }

        // Required-param check (mirrors the backend guard).
        const missing = dashboard.params
            .filter((p) => p.required)
            .filter((p) => {
                const v = this.state.paramValues[p.key];
                return v === undefined || v === null || v === "";
            })
            .map((p) => p.label);
        if (missing.length) {
            return sweetalert.fire({
                icon: "error",
                title: "",
                text: `Missing required: ${missing.join(", ")}`,
            });
        }

        const params: { [key: string]: any } = {};
        for (const p of dashboard.params) {
            params[p.key] = this.formatValue(p, this.state.paramValues[p.key]);
        }

        // First load blocks; a refilter holds the existing tiles at reduced
        // opacity so the layout doesn't jump.
        const firstRun = !this.state.hasRun;
        const requestId = ++this.requestId;
        const requestedName = dashboard.name;
        this.setState({ showLoading: firstRun, refetching: !firstRun }, async () => {
            try {
                const res = await AdminAPI.getDashboardData(
                    requestedName,
                    params,
                    undefined,
                    this.mount,
                );
                // Superseded (dashboard switched / filters re-run) or unmounted.
                if (!this.isCurrent(requestId)) return;
                if (!res || !res.success) {
                    this.setState({ showLoading: false, refetching: false });
                    return sweetalert.fire({
                        icon: "error",
                        title: "",
                        text: (res && res.reason) || "Unable to load this dashboard.",
                    });
                }
                this.setState({
                    data: res.tiles || {},
                    tileErrors: res.tileErrors,
                    generatedAt: res.dashboard?.generatedAt,
                    showLoading: false,
                    refetching: false,
                    hasRun: true,
                });
            } catch (e) {
                console.error(e);
                if (!this.isCurrent(requestId)) return;
                this.setState({ showLoading: false, refetching: false });
                sweetalert.fire({
                    icon: "error",
                    title: "",
                    text: "Unable to load this dashboard.",
                });
            }
        });
    }

    renderParam(param: DashboardParam) {
        const value = this.state.paramValues[param.key];
        let input: JSX.Element;

        if (param.type === "date") {
            input = (
                <DatePicker
                    selected={value instanceof Date ? value : null}
                    onChange={(d) => this.setParam(param.key, d)}
                    className={"form-control"}
                    placeholderText={"Select date..."}
                    isClearable
                />
            );
        } else if (param.type === "facility") {
            input = (
                <Select
                    isClearable
                    placeholder={"Please select..."}
                    options={this.state.facilityNamesMapped}
                    value={
                        this.state.facilityNamesMapped.find((o) => o.value === value) || null
                    }
                    onChange={(e: any) => this.setParam(param.key, e ? e.value : null)}
                />
            );
        } else if (param.type === "select") {
            const options = param.options || [];
            input = (
                <Select
                    isClearable
                    placeholder={"Please select..."}
                    options={options}
                    value={options.find((o) => o.value === value) || null}
                    onChange={(e: any) => this.setParam(param.key, e ? e.value : null)}
                />
            );
        } else if (param.type === "number") {
            input = (
                <input
                    type={"number"}
                    className={"form-control"}
                    value={value ?? ""}
                    onChange={(e) =>
                        this.setParam(
                            param.key,
                            e.target.value === "" ? null : Number(e.target.value),
                        )
                    }
                />
            );
        } else {
            input = (
                <input
                    type={"text"}
                    className={"form-control"}
                    value={value ?? ""}
                    onChange={(e) => this.setParam(param.key, e.target.value)}
                />
            );
        }

        return (
            <div className={"form-group col-12 col-md-4 col-lg-3 mb-2"} key={param.key}>
                <label className={"verlag-bold"}>
                    {param.label}
                    {param.required ? <span className={"text-danger"}> *</span> : null}
                </label>
                {input}
                {param.helpText ? (
                    <small className={"form-text text-muted"}>{param.helpText}</small>
                ) : null}
            </div>
        );
    }

    render() {
        const { dashboards, selected, hasRun, refetching, generatedAt } = this.state;
        // The audience badge is INTERNAL taxonomy — a provider has no use for
        // "also visible internally", and showing it exposes how we classify
        // dashboards to a customer. Staff-only.
        const showAudience = this.mount === "internal";
        const options = dashboards.map((d) => ({
            label: d.displayName,
            value: d.name,
        }));

        return (
            <React.Fragment>
                <Overlay show_loading={this.state.showLoading} />
                <div className="container-fluid">
                    {/* ONE filter row above everything it scopes — never
                        per-tile filters. All tiles re-render on the same slice. */}
                    <div className={"row"}>
                        <div className="col-12 pt-2">
                            <div className="card mb-2">
                                <div className="card-header verlag-bold">
                                    <h4>Dashboards</h4>
                                </div>
                                {/* viz-filters: stacks every param label above its
                                    field and gives the date wrappers a real width.
                                    See the filter-row block in tiles/viz.css. */}
                                <div className="card-body viz-filters">
                                    {/* Bootstrap's default align-items: stretch is
                                        what keeps the LABELS on one line: every
                                        column starts its content at the top of the
                                        row. Do not put align-items-end back here —
                                        bottom-aligning makes each column's start
                                        depend on how tall its content is, so a param
                                        whose helpText wraps to two lines rides up and
                                        drags its label out of the row. Ragged
                                        whitespace under the shorter columns is the
                                        correct trade. Only visible at >=768px; below
                                        that every param is col-12 and stacks. */}
                                    <div className={"row"}>
                                        <div className={"form-group col-12 col-md-4 col-lg-3 mb-2"}>
                                            <label className={"verlag-bold"}>Dashboard</label>
                                            <Select
                                                isClearable
                                                placeholder={"Select a dashboard..."}
                                                options={options}
                                                // Show WHO can see each dashboard in the list
                                                // itself (staff only). This is what makes the
                                                // separate provider page unnecessary for staff.
                                                formatOptionLabel={
                                                    showAudience
                                                        ? (opt: any) => {
                                                              const d = dashboards.find((x) => x.name === opt.value);
                                                              return (
                                                                  <span
                                                                      style={{
                                                                          display: "flex",
                                                                          alignItems: "center",
                                                                          justifyContent: "space-between",
                                                                          gap: "0.5rem",
                                                                      }}
                                                                  >
                                                                      <span>{opt.label}</span>
                                                                      {d ? <AudienceBadge audience={d.audience} /> : null}
                                                                  </span>
                                                              );
                                                          }
                                                        : undefined
                                                }
                                                value={
                                                    selected
                                                        ? {
                                                              label: selected.displayName,
                                                              value: selected.name,
                                                          }
                                                        : null
                                                }
                                                onChange={(e: any) =>
                                                    e ? this.onSelectDashboard(e.value) : this.clear()
                                                }
                                            />
                                        </div>
                                        {selected
                                            ? selected.params.map((p) => this.renderParam(p))
                                            : null}
                                    </div>
                                    {selected && selected.description ? (
                                        <p className={"viz-filter-description mt-1"}>
                                            {selected.description}
                                        </p>
                                    ) : null}
                                </div>
                                <div className="card-footer">
                                    <button
                                        className={"btn immySubmitButtonOutline"}
                                        disabled={!selected || refetching}
                                        onClick={this.run}
                                    >
                                        {hasRun ? "Refresh" : "View Dashboard"}
                                    </button>
                                    {generatedAt ? (
                                        <span className={"ml-3 text-muted"}>
                                            Generated {moment(new Date(generatedAt)).isValid()
                                                ? moment(new Date(generatedAt)).format("M/D/YYYY h:mm A")
                                                : generatedAt}
                                        </span>
                                    ) : null}
                                    <button
                                        className="btn mr-md-5 float-right verlag-bold immyClearButtonOutline"
                                        onClick={this.clear}
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {selected && hasRun ? (
                        <DashboardHost
                            name={selected.name}
                            data={this.state.data}
                            tileErrors={this.state.tileErrors}
                            params={this.state.paramValues}
                            loading={refetching}
                        />
                    ) : null}
                </div>
            </React.Fragment>
        );
    }
}
